<script lang="ts">
  import { getContext } from 'svelte';
  import { Button } from '@/lib/components/ui/button';
  import ConfirmationPopover from '@/MyFitness/Today/ConfirmationPopover.svelte';
  import SendToStravaButton from '@/MyFitness/Today/SendToStravaButton.svelte';
  import ShareActivityButton from '@/MyFitness/Today/ShareActivityButton.svelte';
  import SendToWorkoutPlayerButton from '@/MyFitness/Today/SendToWorkoutPlayerButton.svelte';
  import CommentsButton from '@/Comments/CommentsButton.svelte';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { deleteActivity, downloadActivityFitFile, hideActivity, starActivity, switchKeptDuplicate, unlinkDuplicate } from '@/Util/api';
  import type { TodaysActivities } from '@/Common/constants';
  import type { MyFitnessStores } from '@/MyFitness/stores';
  import { notifyApiError } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';
  import { activityMessagingEnabled, isOwnActivity, type DashboardActivity } from './activityCardUtils';

  export let exercise: DashboardActivity;
  /** Called after the activity has been deleted server-side, so the list can drop the card. */
  export let onDelete: (exercise: Exercise) => void = () => {};
  /** Called whenever a toggle changed a field the list also renders. */
  export let onChange: (exercise: Exercise, changes: Partial<Exercise>) => void = () => {};
  /**
   * Refetch the current page. Needed by the actions whose effect reaches activities other than
   * this one (the rest of a duplicate group), which the dashboard may not even have loaded.
   */
  export let onRefresh: () => void = () => {};
  /** Off in the compact grid view, where there's only room for the icon actions. */
  export let showSendButtons: boolean = true;

  const commentCounts = getContext<MyFitnessStores | undefined>('myFitness')?.commentCounts;

  // Every management action below is scoped to the calling user server-side, so on the coach and
  // squad dashboards -- which list other athletes' rides -- only the comment bubble survives.
  $: canManage = isOwnActivity(exercise);
  $: isStarred = Activity.isStarred(exercise);
  $: isFlagged = Activity.isFlagged(exercise);
  $: isRecorded = Activity.isRecorded(exercise);
  $: isHidden = Activity.isHidden(exercise);
  $: isDuplicate = Activity.isDuplicate(exercise);
  $: canDelete = canManage && Activity.canDelete(exercise);
  $: canFavourite = canManage && Activity.canFavorite(exercise);
  $: canFlag = canManage && !exercise?.state?.includes('no_flag');
  $: showComments = Boolean(exercise?.path && commentCounts && activityMessagingEnabled(exercise));
  $: garminUrl = canManage ? Activity.getGarminLink(exercise) : null;

  let busy = false;

  function apply(changes: Partial<Exercise>) {
    exercise = { ...exercise, ...changes };
    onChange(exercise, changes);
  }

  /**
   * SendToStravaButton writes its result back through a TodaysActivities store rather than a
   * callback. The dashboard has no such store per card, so hand it a one-activity view onto this
   * card that funnels the update into onChange like everything else here.
   */
  const stravaTarget: TodaysActivities = {
    get activities() {
      return [exercise];
    },
    update: (updater) => {
      const updated = updater([exercise])[0];
      if (updated) apply(updated);
    },
  };

  function favourite() {
    if (!exercise?.path) return;
    starActivity(exercise.path, isStarred);
    apply({ favorite: !isStarred });
  }

  function flag() {
    Activity.toggleFlag(exercise);
    apply({ flag: !isFlagged });
  }

  function hide() {
    if (!exercise?.path) return;
    hideActivity(exercise.path, isHidden);
    // Hiding an activity also breaks whatever it was matched to, the same way the Today card does.
    apply({ enabled: isHidden, matched_activity: isHidden ? exercise.matched_activity : undefined });
  }

  async function unlink() {
    if (!exercise?.path) return;
    busy = true;
    await unlinkDuplicate(exercise.path);
    busy = false;
    apply({ duplicate_group_id: undefined, is_currently_selected_duplicate: false });
  }

  async function keepThisDuplicate() {
    if (!exercise?.path) return;
    busy = true;
    await switchKeptDuplicate(exercise.path);
    busy = false;
    // The rest of the duplicate group changes too, and it isn't necessarily on this page.
    onRefresh();
  }

  let downloadIcon: string = 'fa-download';
  $: exercise, (downloadIcon = 'fa-download');

  async function download() {
    if (!exercise?.path ) return;
    downloadIcon = 'fa-spinner-third animate-spin';
    const result = await downloadActivityFitFile(exercise.path, exercise.name ?? '');
    downloadIcon = result.success ? 'fa-check' : 'fa-xmark';
    if (!result.success) notifyApiError(result, { code: ErrorCode.ACTIVITY_DOWNLOAD_FAILED, message: "Could not download this activity's file. Please try again in a moment." });
  }

  async function confirmDelete() {
    if (!exercise?.path) return;
    await deleteActivity(exercise.path);
    onDelete(exercise);
  }
</script>

<div class="flex flex-row flex-wrap items-center gap-1">
  {#if showSendButtons && canManage}
    <SendToStravaButton activity={exercise} todaysActivities={stravaTarget} disabled={busy} />
    <ShareActivityButton activity={exercise} disabled={busy} />
    {#if garminUrl}
      <!-- Font Awesome has no Garmin brand glyph, so use the provider logo the rest of the app does. -->
      <Button size="sm" variant="outline" href={garminUrl} target="garmin" class="text-foreground no-underline" on:click={(e) => e.stopPropagation()}>
        <img src="/assets/global/img/providers/garmin.svg" alt="" class="h-3 w-3 mr-1" aria-hidden="true" />Open in Garmin
      </Button>
    {/if}
    {#if exercise?.workout_id}
      <SendToWorkoutPlayerButton disabled={busy} workout={{ ...exercise, path: exercise.workout_id }} />
    {/if}
  {/if}

  {#if showComments && exercise?.path}
    <CommentsButton
      activityPath={exercise.path}
      activityName={exercise.name}
      total={$commentCounts ? $commentCounts[exercise.path]?.total : 0}
      unread={$commentCounts ? $commentCounts[exercise.path]?.unread : 0}
    />
  {/if}

  {#if canFavourite}
    <Button variant="ghost" size="icon" title={isStarred ? 'Remove from favourites' : 'Add to favourites'} on:click={(e) => { e.stopPropagation(); favourite(); }}>
      <i class={`${isStarred ? 'fa-solid text-amber-500' : 'fa-regular text-muted-foreground'} fa-star fa-lg`} aria-hidden="true"></i>
    </Button>
  {/if}

  {#if isRecorded && canFlag}
    <Button variant="ghost" size="icon" title={isFlagged ? 'Unflag this activity' : 'Flag the breakthrough as invalid'} on:click={(e) => { e.stopPropagation(); flag(); }}>
      <i class={`${isFlagged ? 'fa-solid text-[#F04B2E]' : 'fa-regular text-muted-foreground'} fa-flag fa-lg`} aria-hidden="true"></i>
    </Button>
  {/if}

  {#if canManage}
    {#if isDuplicate}
      <Button variant="ghost" size="icon" disabled={busy} title="Unlink from its duplicate group" on:click={(e) => { e.stopPropagation(); unlink(); }}>
        <i class="fa-regular fa-link-slash text-muted-foreground fa-lg" aria-hidden="true"></i>
      </Button>
      <Button variant="ghost" size="icon" disabled={busy} title="Keep this copy instead" on:click={(e) => { e.stopPropagation(); keepThisDuplicate(); }}>
        <i class="fa-regular fa-swap text-muted-foreground fa-lg" aria-hidden="true"></i>
      </Button>
    {:else}
      <Button variant="ghost" size="icon" title={isHidden ? 'Include in fitness calculations' : 'Hide from fitness calculations'} on:click={(e) => { e.stopPropagation(); hide(); }}>
        <i class={`${isHidden ? 'fa-solid' : 'fa-regular'} fa-eye-slash text-muted-foreground fa-lg`} aria-hidden="true"></i>
      </Button>
    {/if}
  {/if}

  {#await (canManage ? Activity.canDownload(exercise) : Promise.resolve(false)) then canDownload}
    {#if canDownload}
      <Button variant="ghost" size="icon" title="Download the original FIT file" on:click={(e) => { e.stopPropagation(); download(); }}>
        <i class={`fa-regular ${downloadIcon} text-muted-foreground fa-lg`} aria-hidden="true"></i>
      </Button>
    {/if}
  {/await}

  {#if canDelete}
    <ConfirmationPopover title="Delete this activity?" onConfirm={confirmDelete} let:builder>
      <Button variant="ghost" size="icon" title="Delete this activity" builders={[builder]} on:click={(e) => e.stopPropagation()}>
        <i class="fa-regular fa-trash text-muted-foreground fa-lg" aria-hidden="true"></i>
      </Button>
    </ConfirmationPopover>
  {/if}
</div>
